import { useTheme } from "@mui/material/styles";
import { DisclaimerLink } from "@portal/app/health/_components/DreamSearchPage";
import Tooltip from "@mui/material/Tooltip";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { dreamThrottle } from "@portal/app/_common/lib/dreamThrottle";

export const SponsoredResultsDisclaimer = () => {
  const theme = useTheme();
  const analytics = useMixpanel();
  const ref = useRef<HTMLSpanElement>(null);
  const hasTrackedVisibility = useRef(false);

  const trackEvent = useCallback(
    (eventName: MixpanelEventName) => {
      void analytics.track(eventName);
    },
    [analytics]
  );

  // Create throttled versions of our tracking functions
  const throttledHoverTrack = useMemo(
    () => dreamThrottle(() => trackEvent("Dream Search - Sponsored Disclaimer - Hovered")),
    [trackEvent]
  );

  const throttledClickTrack = useMemo(
    () => dreamThrottle(() => trackEvent("Dream Search - Clicked Sponsored Disclaimer")),
    [trackEvent]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !ref.current) {
      return;
    }

    const element = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedVisibility.current) {
          void analytics.track("Dream Search - Sponsored Disclaimer - Scrolled Into View");
          hasTrackedVisibility.current = true;
        }
      },
      {
        threshold: 1,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [analytics]);

  return (
    <Tooltip
      title="These programs match your search but may not be an exhaustive list. Schools may pay Dreambound for visibility on our site."
      enterTouchDelay={0}
      arrow
    >
      <span ref={ref} onMouseEnter={throttledHoverTrack} onClick={throttledClickTrack}>
        <DisclaimerLink>Some results are sponsored.</DisclaimerLink>
        <FontAwesomeIcon icon={faCircleInfo} color={theme.palette.primary.main} />
      </span>
    </Tooltip>
  );
};
